import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, GALLERY_CATS } from "../data/site";

export default function GalleryGrid() {
  const [cat, setCat] = useState("tutti");
  const [lightbox, setLightbox] = useState(null);

  const items = GALLERY.filter((g) => cat === "tutti" || g.cat === cat);

  const openAt = (i) => setLightbox(i);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, items.length]);

  return (
    <div>
      <div className="flex flex-wrap gap-2.5 mb-10" data-testid="gallery-filters">
        {GALLERY_CATS.map((c) => (
          <button
            key={c.id}
            data-testid={`gallery-filter-${c.id}`}
            onClick={() => setCat(c.id)}
            className={`chip ${cat === c.id ? "chip-active" : ""}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]" data-testid="gallery-grid">
        {items.map((g, i) => (
          <motion.button
            key={g.src + i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.07 }}
            onClick={() => openAt(i)}
            data-testid={`gallery-item-${i}`}
            className="group relative mb-4 block w-full overflow-hidden rounded-2xl border border-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
          >
            <div className={`${g.ratio} w-full`}>
              <img
                src={g.src}
                alt={g.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
            </div>
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/92 backdrop-blur-sm p-4 md:p-10"
            onClick={() => setLightbox(null)}
          >
            <button
              data-testid="lightbox-close"
              aria-label="Chiudi"
              onClick={() => setLightbox(null)}
              className="absolute top-5 right-5 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
            >
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev"
              aria-label="Precedente"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i - 1 + items.length) % items.length);
              }}
              className="absolute left-4 md:left-8 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="lightbox-next"
              aria-label="Successiva"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i + 1) % items.length);
              }}
              className="absolute right-4 md:right-8 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
            >
              <ChevronRight size={22} />
            </button>
            <motion.figure
              key={items[lightbox].src}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-h-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={items[lightbox].src}
                alt={items[lightbox].alt}
                className="max-h-[82vh] w-auto max-w-full rounded-xl object-contain"
              />
              <figcaption className="mt-4 text-center text-xs text-white/50">
                {items[lightbox].alt}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
